import Vue from 'vue'
import Vuex from 'vuex'
import router from '@/router'
import { cloneDeep } from 'lodash'
import { DEFAULT_LANG, isMd } from '@/helpers/constant'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    general: {},
    deviceSize: {
      width: 0,
      height: 0
    },
    vinSearchData: {},
    oilId: '',
    model: {},
    dealer: {},
    aliasParamCity: '',
    aliasParamDealer: '',
    workpackages: '',
    diyaData: {
      brand: '',
      firstName: '',
      lastName: '',
      licensePlate: '',
      model: '',
      vin: ''
    },
    currentLang: DEFAULT_LANG,
    languages: []
  },
  getters: {
    getModel: state => state.route.params.model,
    getYear: state => state.route.params.year,
    getModification: state => state.route.params.modification,
    getPeriod: state => state.route.query.period,
    getCity: state => state.route.query.city,
    getDealer: state => state.route.query.dealer,
    isMobile: state => state.deviceSize.width < 768,
    isTablet: state => state.deviceSize.width >= 768 && state.deviceSize.width < 1280,
    isDesktop: state => state.deviceSize.width >= 1280,
    getOilId: state => state.oilId,
    getAliasParamCity: state => state.aliasParamCity,
    getAliasParamDealer: state => state.aliasParamDealer,
    getWorkpackages: state => state.workpackages,
    getDiyaData: state => state.diyaData
  },
  actions: {
    async fetchGeneralData ({ commit }) {
      try {
        const { data } = await window.axios.get('/general')

        commit('setGeneralData', data.data)
      } catch (err) {
        console.error('FAILED: fetchGeneralData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchModelPageData () {
      try {
        const { data } = await window.axios.get('/model')
        return data.data
      } catch (err) {
        console.error('FAILED: fetchModelPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchYearPageData (store, { model }) {
      try {
        const { data } = await window.axios.get('/year', {
          params: { carModelId: model },
          query: {
            lang: this.currentLang
          }
        })
        return data.data
      } catch (err) {
        console.error('FAILED: fetchYearPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchModificationPageData (store, { year, transmission, drive, fuel, volume, additional, batteryCapacity }) {
      try {
        const { data } = await window.axios.get('/modification', {
          params: { generationId: year, transmission, drive, fuel, volume, additional, battery: batteryCapacity },
          query: {
            lang: this.currentLang
          }
        })
        return data.data
      } catch (err) {
        console.error('FAILED: fetchModificationPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchTechMapPageData (store, { modification }) {
      try {
        const { data } = await window.axios.get('/tech-map', {
          params: { modificationId: modification },
          query: {
            lang: this.currentLang
          }
        })
        return data.data
      } catch (err) {
        console.error('FAILED: fetchTechMapPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchTechMapResultsData (store, { period, dealer, oil, workpackages }) {
      try {
        const { data } = await window.axios.get('/tech-map-result', {
          params: { exploitationPeriodId: period, dealerId: dealer, oil, workpackages },
          query: {
            lang: this.currentLang
          }
        })
        return data.data
      } catch (err) {
        console.error('FAILED: fetchTechMapResultsData')
        router.push({ name: 'not-found' })
      }
    },
    async fetchServiceRequestPageData (store, { period, dealer, oil, workpackages }) {
      try {
        const { data } = await window.axios.get('/service-request', {
          params: { exploitationPeriodId: period, dealerId: dealer, oil, workpackages },
          query: {
            lang: this.currentLang
          }
        })
        return data.data
      } catch (err) {
        console.error('FAILED: fetchServiceRequestPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async submitServiceRequest (store, { submitUrl, form, formName }) {
      let params = {}
      if (window.iframe && formName === 'ServiceRequestPage') {
        params = {
          iframe: 1
        }
      }

      const { data } = await window.axios.post(submitUrl, form, {
        params
      })
      return data
    },
    async fetchMarketingPageData () {
      try {
        const { data } = await window.axios.get('/marketing-agree')
        return data.data
      } catch (err) {
        console.error('FAILED: fetchPolicyPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchPolicyPageData () {
      try {
        const { data } = await window.axios.get('/policy')
        return data.data
      } catch (err) {
        console.error('FAILED: fetchPolicyPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async fetchThankYouPageData () {
      try {
        const { data } = await window.axios.get('/thank-you')
        return data.data
      } catch (err) {
        console.error('FAILED: fetchThankYouPageData')
        console.error(err)
        router.push({ name: 'not-found' })
      }
    },
    async submitVinCode (store, { form }) {
      const { data } = await window.axios.post('/vin-search', form)
      return data
    }
  },
  mutations: {
    SET_MODEL (state, data) {
      state.model = { ...data }
    },
    SET_DEALER (state, data) {
      state.dealer = { ...data }
    },
    setCurrentLang (state, payload) {
      state.currentLang = payload.toLowerCase()
    },
    setGeneralData (state, newGeneralData) {
      state.general = {
        ...cloneDeep(state.general),
        ...newGeneralData
      }
      if (newGeneralData && newGeneralData.locales) {
        state.languages = newGeneralData.locales
          .map(l => ({
            code: l.code,
            label: l.name,
            visible: !(isMd && l.code === 'uk')
          }))
      }
    },
    setDeviceWidth (state, width) {
      state.deviceSize.width = width
    },
    setDeviceHeight (state, height) {
      state.deviceSize.height = height
    },
    SET_VIN_SEARCH_DATA (state, data) {
      state.vinSearchData = { ...data }
    },
    SET_OIL_ID (state, value) {
      state.oilId = value
    },
    SET_ALIAS_PARAM_CITY (state, value) {
      state.aliasParamCity = value
    },
    SET_ALIAS_PARAM_DEALER (state, value) {
      state.aliasParamDealer = value
    },
    SET_WORKPACKAGES (state, value) {
      state.workpackages = value
    },
    setDiyaData (state, data) {
      state.diyaData = data
    }
  }
})
